// Ficha al sonar (D72): cuando 3CX timbra la extension de esta persona (en su app o telefono de escritorio, no solo en
// el telefono del CRM), aparece quien llama con su ficha del CRM. Si la llamada se pierde, ofrece crear la tarea de
// devolverla. Los eventos llegan de la central por el servicio de tiempo real.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { buscarPorNumero, crearTarea } from '../api/crm';
import { type Llamada3cx } from '../api/llamadas3cx';
import { accionPuente } from '../api/puente';
import { type ContactoLookup } from '../api/types';
import { usePhone } from '../hooks';
import { Icon } from '../lib/icons';
import { formatPhone } from '../lib/phone-number';
import { alRecibir } from '../lib/tiempoReal';
import { toast } from './toasts';

export function FichaEntrante() {
  const s = usePhone();
  const nav = useNavigate();
  const [l, setL] = useState<Llamada3cx | null>(null);
  const [contacto, setContacto] = useState<ContactoLookup | null>(null);
  const miExt = s.config.extension;

  useEffect(
    () =>
      alRecibir((e) => {
        if (e.tipo !== 'llamada3cx') return;
        const x = (e as unknown as { llamada: Llamada3cx }).llamada;
        if (!miExt || x.extension !== miExt) return;
        // El telefono del CRM ya muestra su propia llamada: no se duplica.
        if (s.call && s.call.numero.replace(/\D/g, '').endsWith(x.numero.replace(/\D/g, '').slice(-7))) return;
        setL((actual) => (actual && actual.id !== x.id && x.estado !== 'sonando' ? actual : x));
      }),
    [miExt, s.call],
  );

  useEffect(() => {
    if (!l || l.estado !== 'sonando') return;
    let vivo = true;
    setContacto(null);
    void buscarPorNumero(l.numero).then((c) => vivo && setContacto(c));
    return () => {
      vivo = false;
    };
  }, [l?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (l?.estado !== 'terminada') return;
    const t = setTimeout(() => setL(null), 4000);
    return () => clearTimeout(t);
  }, [l]);

  // D80: control de la llamada desde el CRM aunque se atienda en el telefono o la app de 3CX (Puente XmarteK).
  const [transferirA, setTransferirA] = useState<string | null>(null);
  const [casoPegado, setCasoPegado] = useState<string | null>(null);
  const [pegando, setPegando] = useState<string | null>(null);
  const puente = async (accion: string, datos: Record<string, unknown>, ok: string) => {
    try {
      await accionPuente(accion, datos);
      toast(ok);
      return true;
    } catch (e) {
      toast((e as Error).message, 'error');
      return false;
    }
  };

  if (!l) return null;
  const nombre = contacto ? `${contacto.nombre} ${contacto.apellido ?? ''}`.trim() : l.nombre || formatPhone(l.numero);
  const ESTADO: Record<Llamada3cx['estado'], string> = { marcando: 'Marcando', sonando: 'Te llama en 3CX', hablando: 'En llamada', transfiriendo: 'Transfiriendo', terminada: 'Terminó', perdida: 'Llamada perdida' };
  const devolver = async () => {
    try {
      await crearTarea({ titulo: `Devolver llamada a ${nombre}`, vence: new Date(Date.now() + 3600_000).toISOString(), personaId: contacto?.id ?? null, cuerpo: `Llamada perdida de ${formatPhone(l.numero)}${l.cola ? ` (cola ${l.cola.nombre || l.cola.extension})` : ''}.` });
      toast('Tarea creada: devolver la llamada en la próxima hora.');
      setL(null);
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };
  return (
    <aside className={`ficha-3cx ${l.estado}`} role="status" aria-live="polite">
      <span className="ficha-3cx-e">
        <Icon name={l.estado === 'perdida' ? 'phoneMiss' : 'phoneIn'} /> {ESTADO[l.estado]}
        {l.cola ? ` · cola ${l.cola.nombre || l.cola.extension}` : ''}
      </span>
      <b className="ficha-3cx-n">{nombre}</b>
      <span className="sub">
        {formatPhone(l.numero)}
        {contacto?.empresa ? ` · ${contacto.empresa}` : ''}
        {!contacto && l.estado === 'sonando' ? ' · no está en el CRM' : ''}
      </span>
      <span className="ficha-3cx-a">
        {contacto ? (
          <button className="btn sm primary" type="button" onClick={() => nav(`/personas/${contacto.id}`)}>
            Abrir ficha
          </button>
        ) : null}
        {l.estado === 'hablando' ? (
          pegando != null ? (
            <form
              style={{ display: 'flex', gap: 6 }}
              onSubmit={(e) => {
                e.preventDefault();
                const caso = pegando.trim();
                if (caso) void puente('datos', { datos: { caso, contacto: contacto?.id ?? '' } }, 'Caso pegado: quien reciba la llamada lo verá').then((ok) => ok && (setCasoPegado(caso), setPegando(null)));
              }}
            >
              <input autoFocus value={pegando} onChange={(e) => setPegando(e.target.value)} placeholder="Número del caso" style={{ width: 150 }} />
              <button className="btn sm primary">Pegar</button>
              <button className="btn sm ghost" type="button" onClick={() => setPegando(null)}>
                Cancelar
              </button>
            </form>
          ) : transferirA == null ? (
            <>
              <button className="btn sm" type="button" onClick={() => setTransferirA('')}>
                Transferir
              </button>
              <button className="btn sm" type="button" onClick={() => setPegando(casoPegado ?? '')}>
                {casoPegado ? `Caso ${casoPegado}` : 'Pegar caso'}
              </button>
              <button className="btn sm danger" type="button" onClick={() => void puente('colgar', {}, 'Llamada colgada')}>
                Colgar
              </button>
            </>
          ) : (
            <form
              style={{ display: 'flex', gap: 6 }}
              onSubmit={(e) => {
                e.preventDefault();
                void puente('transferir', { destino: transferirA }, `Transferida a ${transferirA}`).then((ok) => ok && setTransferirA(null));
              }}
            >
              <input autoFocus value={transferirA} onChange={(e) => setTransferirA(e.target.value)} placeholder="Extensión o número" style={{ width: 150 }} />
              <button className="btn sm primary">Transferir</button>
              <button className="btn sm ghost" type="button" onClick={() => setTransferirA(null)}>
                Cancelar
              </button>
            </form>
          )
        ) : null}
        {l.datos?.public_caso ? (
          <button className="btn sm" type="button" onClick={() => nav(`/bandeja/casos?q=${encodeURIComponent(l.datos?.public_caso ?? '')}`)}>
            Caso {l.datos.public_caso}
          </button>
        ) : null}
        {l.estado === 'perdida' ? (
          <>
            <button className="btn sm" type="button" onClick={() => void devolver()}>
              Crear tarea de devolver
            </button>
          </>
        ) : null}
        <button className="btn sm ghost" type="button" aria-label="Cerrar" onClick={() => setL(null)}>
          <Icon name="x" />
        </button>
      </span>
    </aside>
  );
}
